import { memo } from 'react'
import {
  Post,
  useGetApiV1GroupsByGroupIdCategoriesQuery,
  useGetApiV1GroupsByGroupIdQuery,
} from '@wellesley/openapi'
import { AppsLink } from '@wellesley/router'
import Footer from './Footer'
import Header from './Header'
import { cn } from '@wellesley/ui'
import addEmojisToText from 'helpers/addEmojisToText'
import { CategoryAndTagsLinks } from '../CategoryAndTagsLinks'
import { replaceTagsByWhitespace } from '../htmlUtils'
import { useGetApiV1PostsByPostIdQuery } from '../../feed/api'
import { UserAvatar } from '@wellesley/user'
import { getUserImage } from 'helpers/getUserImage'

type ForumPostSearchResultsCardProps = {
  post: Post
}

const ForumPostSearchResultsCard = memo<ForumPostSearchResultsCardProps>(
  ({ post }: ForumPostSearchResultsCardProps) => {
    const { data: group } = useGetApiV1GroupsByGroupIdQuery(
      { groupId: post.groupId! },
      { skip: !post.groupId },
    )
    const { data: categories } = useGetApiV1GroupsByGroupIdCategoriesQuery(
      { groupId: post.groupId! },
      { skip: !post.groupId },
    )
    const { data: originalPost } = useGetApiV1PostsByPostIdQuery(
      {
        postId: post.path[0] || '',
        includePublisher: null,
        withRepostedBy: null,
        withTrends: null,
      },
      { skip: !post.path[0] },
    )

    if (!group || !categories) {
      return null
    }

    const category = categories.find((cat) => cat.id === post.ownerId)

    return (
      <div className="pt-s">
        <div className={cn('flex', originalPost ? 'gap-xxs' : '')}>
          {originalPost ? (
            <div className="flex-shrink-0">
              <UserAvatar
                name={addEmojisToText(
                  originalPost.author.displayName,
                  originalPost.emojis,
                )}
                size={24}
                className="z-[1]"
                imgUrl={getUserImage({
                  user: originalPost.author,
                  type: 'avatar',
                })}
              />
            </div>
          ) : null}
          <div className="flex-1">
            <AppsLink
              to={'/g/:groupname/forum/posts/:postId'}
              props={{ groupname: group.groupName, postId: post.id }}
              data-testid="forum-post-link"
              className={'gap-xxs relative flex items-center'}
              onClick={(e) => e.preventDefault()}
            >
              <div className="flex w-full flex-1 flex-col">
                <Header {...(originalPost || post)} />
                {/* TODO: add participants when implemented */}
              </div>
            </AppsLink>
            {/* TODO: add attachments when implemented */}
            <CategoryAndTagsLinks tags={post.tags} category={category} />
            <div className={cn('my-xs', originalPost ? 'ml-l' : '')}>
              <div className="gap-xxs relative flex items-start">
                {originalPost ? (
                  <UserAvatar
                    name={addEmojisToText(post.author.displayName, post.emojis)}
                    className="z-[1] flex-shrink-0"
                    imgUrl={getUserImage({
                      user: post.author,
                      type: 'avatar',
                    })}
                  />
                ) : null}
                {originalPost ? (
                  <div
                    className="absolute z-[1] hidden border-l-[1px] border-gray-400 md:block md:rounded-bl-[10px] md:border-b-[1px]"
                    style={{
                      top: -12,
                      left: -17,
                      width: 17,
                      height: 32,
                    }}
                  />
                ) : null}
                <div
                  data-testid="post-text"
                  className={cn(
                    'post inline-block text-base text-gray-500 [&_a:hover]:!no-underline [&_a:hover_span]:!underline',
                    'line-clamp-5 overflow-hidden text-ellipsis',
                  )}
                  dangerouslySetInnerHTML={{
                    __html: addEmojisToText(
                      replaceTagsByWhitespace(post.text),
                      post.emojis,
                    ),
                  }}
                />
              </div>
            </div>
            <div className={cn(originalPost ? 'ml-l' : '')}>
              <Footer {...post} />
            </div>
          </div>
        </div>
        <div className="mt-xs h-[1px] w-full border-b border-dashed border-gray-200" />
      </div>
    )
  },
)

ForumPostSearchResultsCard.displayName = 'ForumPostSearchResultsCard'

export default ForumPostSearchResultsCard
